/**
 * Chat query operations - Create
 */

import { v4 as uuidv4 } from 'uuid';
import type { UserId } from '../../types';
import { getSupabaseClient } from '../../supabase-client';
import type { StoredChat } from '../types';
import { logger } from '../../../logger';

/**
 * Create a new chat with an auto-generated UUID
 */
export async function createChat(
  userId: UserId,
  title?: string
): Promise<StoredChat> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_chats')
      .insert({
        id: uuidv4(),
        user_id: userId,
        title: title || 'New Chat',
        status: 'active',
        created_at: now,
        updated_at: now,
        last_message_at: now,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredChat;
  } catch (error) {
    logger.error('error creating chat', error, { op: 'createChat' });
    throw error;
  }
}

/**
 * Create a new chat with a specific ID (e.g., Slack DM ID)
 * Used for integrations with external systems that provide their own IDs
 */
export async function createChatWithId(
  chatId: string,
  userId: UserId,
  title?: string
): Promise<StoredChat> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_chats')
      .insert({
        id: chatId,
        user_id: userId,
        title: title || 'New Chat',
        status: 'active',
        created_at: now,
        updated_at: now,
        last_message_at: now,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredChat;
  } catch (error) {
    logger.error('error creating chat with id', error, { op: 'createChatWithId' });
    throw error;
  }
}
